import { useCallback, useEffect, useId, useRef, useState, type PointerEvent } from 'react'
import { cn } from '../lib/cn'
import { Button } from './button'

/**
 * Área de traço: a pessoa assina com o dedo ou o mouse e o componente devolve o PNG (data URL, fundo
 * transparente) a cada traço terminado, ou `null` ao limpar. Linha de base em fio, com a dica acima dela
 * até o primeiro traço; "Limpar" em texto. O traço é em ink; o componente não sabe o que está sendo
 * assinado (T15): quem usa diz o rótulo e o que vale.
 */
export interface SignaturePadProps {
  /** Nome acessível da área ("Sua assinatura"). */
  label: string
  /** A dica sobre a linha até o primeiro traço. Padrão: "Assine acima da linha". */
  placeholder?: string
  clearLabel?: string
  /** Altura da área em px. Padrão 180. */
  height?: number
  disabled?: boolean
  /** O PNG do traço (data URL) depois de cada traço, ou `null` ao limpar. */
  onChange: (png: string | null) => void
  className?: string
}

/** Espessura do traço em px CSS. */
const STROKE_WIDTH = 2.2

export function SignaturePad({
  label,
  placeholder = 'Assine acima da linha',
  clearLabel = 'Limpar',
  height = 180,
  disabled = false,
  onChange,
  className,
}: SignaturePadProps) {
  const id = useId()
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)
  const last = useRef<{ x: number; y: number } | null>(null)
  const [hasStrokes, setHasStrokes] = useState(false)

  /** Ajusta o canvas ao tamanho em CSS e à densidade da tela, mantendo o que já foi desenhado. */
  const fit = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ratio = window.devicePixelRatio || 1
    const width = canvas.clientWidth
    if (width === 0) return
    const snapshot = canvas.width > 0 && canvas.height > 0 && hasStrokes ? canvas.toDataURL() : null
    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    ctx.lineWidth = STROKE_WIDTH
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = getComputedStyle(canvas).color || '#1a1a1a'
    if (snapshot) {
      const image = new Image()
      image.onload = () => ctx.drawImage(image, 0, 0, width, height)
      image.src = snapshot
    }
  }, [height, hasStrokes])

  useEffect(() => {
    fit()
    const canvas = canvasRef.current
    if (!canvas || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => fit())
    observer.observe(canvas)
    return () => observer.disconnect()
  }, [fit])

  function point(event: PointerEvent<HTMLCanvasElement>) {
    const rect = event.currentTarget.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  function start(event: PointerEvent<HTMLCanvasElement>) {
    if (disabled) return
    event.preventDefault()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    drawing.current = true
    last.current = point(event)
    const ctx = event.currentTarget.getContext('2d')
    if (!ctx || !last.current) return
    // Um toque sem arrastar também marca: o ponto conta como traço.
    ctx.beginPath()
    ctx.moveTo(last.current.x, last.current.y)
    ctx.lineTo(last.current.x + 0.1, last.current.y + 0.1)
    ctx.stroke()
  }

  function move(event: PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || !last.current) return
    event.preventDefault()
    const ctx = event.currentTarget.getContext('2d')
    if (!ctx) return
    const next = point(event)
    ctx.beginPath()
    ctx.moveTo(last.current.x, last.current.y)
    ctx.lineTo(next.x, next.y)
    ctx.stroke()
    last.current = next
  }

  function end(event: PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return
    drawing.current = false
    last.current = null
    setHasStrokes(true)
    onChange(event.currentTarget.toDataURL('image/png'))
  }

  function clear() {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
    setHasStrokes(false)
    onChange(null)
  }

  return (
    <div data-slot="signature-pad" className={cn('flex flex-col', className)}>
      <div className="relative">
        <canvas
          ref={canvasRef}
          id={id}
          role="img"
          aria-label={label}
          aria-describedby={`${id}-hint`}
          aria-disabled={disabled || undefined}
          data-has-strokes={hasStrokes || undefined}
          className={cn(
            'bg-surface border-hairline-strong text-ink block w-full touch-none border border-b-0',
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-crosshair',
          )}
          style={{ height }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onPointerLeave={(event) => {
            if (drawing.current) end(event)
          }}
        />
        <div className="border-hairline-heavy border-t" aria-hidden="true" />
        <p
          id={`${id}-hint`}
          className={cn(
            'font-body text-label-soft pointer-events-none absolute right-4 bottom-3 left-4 text-center text-[12px] leading-none font-light',
            hasStrokes && 'sr-only',
          )}
        >
          {placeholder}
        </p>
      </div>
      <div className="mt-2 flex justify-end">
        <Button
          type="button"
          variant="text"
          size="sm"
          onClick={clear}
          disabled={disabled || !hasStrokes}
        >
          {clearLabel}
        </Button>
      </div>
    </div>
  )
}
